import type { ReportFact, RevenueBasis } from './types';

/**
 * Normalized financial event the report engine reads.
 * New billing methods add a source type here; they do not hardcode amounts.
 */
export type RevenueSourceType =
  | 'invoice'
  | 'punch_card_redemption'
  | 'punch_card_sale'
  | 'direct_payment'
  | 'product_sale'
  | 'other';

export interface RevenueEvent {
  appointmentId: string | null;
  practitionerId: string | null;
  patientId: string | null;
  serviceId: string | null;
  date: string;
  sourceType: RevenueSourceType;
  billingMethod: string | null;
  amountCents: number;
  currency: string;
  quantity: number;
  commissionable: boolean;
  metadata: {
    punchCardId: string | null;
    invoiceId: string | null;
    clipsRedeemed: number;
    packageClipCount: number | null;
    packageSaleAmountCents: number | null;
    revenuePerClipCents: number | null;
  };
}

const SOURCE_TYPES = new Set<RevenueSourceType>([
  'invoice',
  'punch_card_redemption',
  'punch_card_sale',
  'direct_payment',
  'product_sale',
  'other',
]);

export function inferSourceType(fact: ReportFact): RevenueSourceType {
  if (fact.source_type && SOURCE_TYPES.has(fact.source_type)) return fact.source_type;
  if (fact.fact_kind === 'product_sale') {
    return fact.billing_method === 'punch_card' ? 'punch_card_sale' : 'product_sale';
  }
  if (fact.billing_method === 'punch_card' || fact.punch_card_id) return 'punch_card_redemption';
  if (fact.invoice_id) return 'invoice';
  if (fact.payment_method) return 'direct_payment';
  return 'other';
}

export function isPunchCardSale(fact: ReportFact): boolean {
  return inferSourceType(fact) === 'punch_card_sale';
}

export function isTreatmentFact(fact: ReportFact): boolean {
  return fact.fact_kind === 'appointment';
}

/** Commissionable / recognized treatment amount. Prepaid package sales are 0. */
export function recognizedTreatmentRevenueCents(
  fact: ReportFact,
  basis: RevenueBasis,
): number {
  if (!isTreatmentFact(fact) || isPunchCardSale(fact)) return 0;
  return basis === 'paid' ? fact.paid_amount_cents : fact.invoiced_amount_cents;
}

export function prepaidSaleAmountCents(fact: ReportFact, basis: RevenueBasis): number {
  if (fact.fact_kind !== 'product_sale') return 0;
  return basis === 'paid' ? fact.paid_amount_cents : fact.invoiced_amount_cents;
}

export function factToRevenueEvent(fact: ReportFact, basis: RevenueBasis): RevenueEvent {
  const sourceType = inferSourceType(fact);
  const commissionable = isTreatmentFact(fact) && sourceType !== 'punch_card_sale';
  const amountCents = commissionable
    ? recognizedTreatmentRevenueCents(fact, basis)
    : prepaidSaleAmountCents(fact, basis);
  return {
    appointmentId: fact.appointment_id,
    practitionerId: fact.practitioner_id,
    patientId: fact.patient_id,
    serviceId: fact.service_id,
    // Appointment facts: list_practitioner_report_facts puts completed_at here.
    date: fact.starts_at,
    sourceType,
    billingMethod: fact.billing_method,
    amountCents,
    currency: fact.currency,
    quantity: fact.clips_redeemed ?? (isTreatmentFact(fact) ? 1 : 0),
    commissionable,
    metadata: {
      punchCardId: fact.punch_card_id,
      invoiceId: fact.invoice_id,
      clipsRedeemed: fact.clips_redeemed ?? 0,
      packageClipCount: fact.package_clip_count,
      packageSaleAmountCents: fact.package_sale_amount_cents,
      revenuePerClipCents: fact.revenue_per_clip_cents,
    },
  };
}
